import { money } from "@/lib/api";
import { Link } from "react-router-dom";
import { STATUS_TONE } from "@/components/ObservationDialog";
import { isParent } from "@/lib/roles";

const Stat = ({ label, value, tone = "", testid }) => (
  <div className="bg-white rounded-2xl border border-border p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</p>
    <p className={`mt-1 font-display text-2xl font-bold ${tone}`}>{value}</p>
  </div>
);

const Card = ({ title, children, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-6" data-testid={testid}>
    <h2 className="font-display text-lg font-semibold">{title}</h2>
    <div className="mt-4">{children}</div>
  </div>
);

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const OverviewTab = ({ data, user }) => {
  const s = data.student, a = data.attendance, b = data.billing;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="overview-attendance-stats">
        <Stat label="Attendance rate" value={`${a.attendance_rate}%`} tone="text-[#3B82F6]" testid="stat-rate" />
        <Stat label="Days scheduled" value={a.days_scheduled} testid="stat-scheduled" />
        <Stat label="Days present" value={a.days_present} tone="text-emerald-600" testid="stat-present" />
        <Stat label="Days absent" value={a.days_absent} tone={a.days_absent ? "text-red-500" : ""} testid="stat-absent" />
        <Stat label="Excused / unexcused" value={`${a.excused_absences} / ${a.unexcused_absences}`} testid="stat-excused" />
        <Stat label="Hours in care" value={a.total_hours} testid="stat-hours" />
        <Stat label="Late arrivals" value={`${a.late_arrivals} · ${a.late_arrival_minutes} min`} tone={a.late_arrivals ? "text-amber-600" : ""} testid="stat-late-arrivals" />
        <Stat label="Early pickups" value={`${a.early_pickups} · ${a.early_pickup_minutes} min`} testid="stat-early" />
        <Stat label="Late pickups" value={`${a.late_pickups} · ${a.late_pickup_minutes} min`} tone={a.late_pickups ? "text-amber-600" : ""} testid="stat-late-pickups" />
        {b && <Stat label="Current balance" value={money(b.balance)} tone={b.balance > 0 ? "text-amber-600" : "text-emerald-600"} testid="stat-balance" />}
        {b && <Stat label="Past due" value={`${money(b.past_due)}${b.days_past_due ? ` · ${b.days_past_due}d` : ""}`} tone={b.past_due > 0 ? "text-red-500" : ""} testid="stat-past-due" />}
        {b && <Stat label="Outstanding late fees" value={money(b.outstanding_late_fees)} testid="stat-late-fees" />}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Classroom & schedule" testid="overview-classroom">
          <p className="font-bold">{s.room} <span className="text-muted-foreground font-semibold">· {s.age_group || s.program}</span></p>
          <p className="text-sm text-muted-foreground mt-1">
            {s.teachers.length ? s.teachers.map((t) => `${t.name}${t.primary ? " (primary)" : ""}`).join(", ") : "No teacher assigned yet"}
          </p>
          <div className="mt-4 grid grid-cols-7 gap-1 text-center">
            {DAYS.map((d) => {
              const v = s.schedule?.[d];
              return (
                <div key={d} className={`rounded-xl p-2 text-xs ${v?.on ? "bg-blue-50 text-blue-700" : "bg-muted text-muted-foreground"}`} data-testid={`sched-${d}`}>
                  <p className="font-bold uppercase">{d}</p>
                  {v?.on ? <p className="mt-1 leading-tight">{v.arrive}<br />{v.pickup}</p> : <p className="mt-1">—</p>}
                </div>
              );
            })}
          </div>
          {!s.schedule && <p className="mt-3 text-xs text-muted-foreground">No weekly schedule set — lateness can't be calculated until one is added.</p>}
        </Card>

        <Card title="Missing forms & upcoming documents" testid="overview-forms">
          {data.missing_forms.length === 0 && data.upcoming_documents.length === 0 && <p className="text-sm text-muted-foreground">Nothing outstanding. Nice.</p>}
          <ul className="space-y-2 text-sm">
            {data.missing_forms.map((f) => (
              <li key={f.id} className="flex justify-between gap-3" data-testid={`missing-form-${f.id}`}>
                <span className="font-semibold">{f.title}</span><span className="text-red-500 font-bold">Missing{f.due_date ? ` · due ${f.due_date}` : ""}</span>
              </li>
            ))}
            {data.upcoming_documents.map((d, i) => (
              <li key={`${d.type}-${d.label}-${i}`} className="flex justify-between gap-3">
                <span className="font-semibold">{d.type}: {d.label}</span>
                <span className={`font-bold ${d.overdue ? "text-red-500" : "text-amber-600"}`}>{d.overdue ? "Overdue" : "Due"} {d.due}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Upcoming events & transitions" testid="overview-events">
          {data.upcoming_events.length === 0 && <p className="text-sm text-muted-foreground">No upcoming events for this classroom.</p>}
          <ul className="space-y-2 text-sm">
            {data.upcoming_events.map((e) => (
              <li key={e.id} className="flex justify-between gap-3"><span className="font-semibold">{e.title}</span>
                <span className="text-muted-foreground font-semibold">{e.type} · {e.date}</span></li>
            ))}
          </ul>
        </Card>

        <Card title="Developmental summary" testid="overview-development">
          {Object.keys(data.developmental_summary).length === 0 && <p className="text-sm text-muted-foreground">No observations in this period.</p>}
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.developmental_summary).map(([k, v]) => (
              <span key={k} className={`text-xs font-bold px-3 py-1 rounded-full border ${STATUS_TONE[k] || "bg-muted"}`}>{k} · {v}</span>
            ))}
          </div>
          <div className="mt-4 space-y-3">
            {data.recent_observations.map((o) => (
              <div key={o.id} className="text-sm border-l-4 pl-3 border-blue-200" data-testid={`recent-obs-${o.id}`}>
                <p className="font-bold">{o.category} <span className="text-muted-foreground font-semibold">· {o.status}</span></p>
                <p className="text-muted-foreground mt-0.5 line-clamp-2">{o.description}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Recent incidents or concerns" testid="overview-incidents">
          {data.recent_incidents.length === 0 && <p className="text-sm text-muted-foreground">No incidents logged in the last 30 days.</p>}
          <ul className="space-y-2 text-sm">
            {data.recent_incidents.map((i) => <li key={i.id}><span className="font-bold">{i.date}</span> — {i.detail}</li>)}
          </ul>
        </Card>

        <Card title="Recent achievements" testid="overview-achievements">
          {data.recent_achievements.length === 0 && <p className="text-sm text-muted-foreground">Kudos and “Doing Well” notes will show here.</p>}
          <ul className="space-y-2 text-sm">
            {data.recent_achievements.map((i) => <li key={i.id} className="text-emerald-700 font-semibold">★ {i.detail || i.description}</li>)}
          </ul>
        </Card>
      </div>

      {b && !isParent(user) && b.account_hold && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm font-semibold text-red-700" data-testid="account-hold-banner">
          Account hold: {b.hold_reason || "see billing notes"}. Attendance is not restricted unless enabled in attendance rules.
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Period {data.period.start} → {data.period.end}. Need the full record? Open the <Link to="?tab=attendance" className="underline">Attendance</Link> tab.
      </p>
    </div>
  );
};
